import { notifications } from "@mantine/notifications";
import GlossaryForm from "Components/Admin/AdminOtherDetails/AdminGlossary/GlossaryForm";
import GlossaryView from "Pages/Admin/OtherDetails/AdminGlossaryView";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useGetGlossaryByIdQuery,
  useUpdateGlossaryMutation,
} from "Services/GlossaryApiSlice";
import { GlossaryFormItem } from "Types/Admin/GlossaryFormItem";

type LocationState = {
  item?: GlossaryFormItem;
  mode?: "view" | "edit";
};

interface ImageWithId {
  id: number;
  url: string;
}

const AdminGlossaryEdit: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const plant = process.env.REACT_APP_PLANT || "";

  const state = (location.state || {}) as LocationState;
  const initialItem = state.item ?? null;
  const mode = state.mode ?? "edit";

  const [formItem, setFormItem] = useState<GlossaryFormItem | null>(
    initialItem,
  );

  const { data, isLoading, isError } = useGetGlossaryByIdQuery(
    { id: Number(id), plant },
    { skip: !id || !!initialItem },
  );

  const [updateGlossary, { isLoading: isUpdating }] = useUpdateGlossaryMutation();

  useEffect(() => {
    if (initialItem) {
      setFormItem(initialItem);
      return;
    }

    if (!data) return;

    const result = (data as any)?.results;
    if (!result) return;

    const hdr = result.glossaryHdr || {};

    const imagesWithIds: ImageWithId[] =
      result.glossaryDetList?.map((det: any) => ({
        id: det.id,
        url: det.glossaryImage,
      })) ?? [];

    const imagesPreviewUrls = imagesWithIds.map((img: ImageWithId) => img.url);

    const mapped: GlossaryFormItem = {
      id: hdr.id,
      date: hdr.glossaryDate
        ? new Date(hdr.glossaryDate).toLocaleDateString("en-GB")
        : "",
      title: hdr.glossaryName || "",
      venue: hdr.address || hdr.city || "",
      glossaryDescription: hdr.glossaryDescription || "",
      glossaryDescription2: hdr.glossaryDescription2 || "",
      address: hdr.address || "",
      city: hdr.city || "",
      state: hdr.state || "",
      country: hdr.country || "",
      pin: hdr.pin || "",

      imagesPreviewUrls,
      imagesWithIds,
      section1Title: hdr.section1Title || "",
      section1Description: hdr.section1Description || "",
      section2Title: hdr.section2Title || "",
      section2Description: hdr.section2Description || "",
      section3Title: hdr.section3Title || "",
      section3Description: hdr.section3Description || "",
      section4Title: hdr.section4Title || "",
      section4Description: hdr.section4Description || "",
      section5Title: hdr.section5Title || "",
      section5Description: hdr.section5Description || "",
      section6Title: hdr.section6Title || "",
      section6Description: hdr.section6Description || "",
      section7Title: hdr.section7Title || "",
      section7Description: hdr.section7Description || "",
      section8Title: hdr.section8Title || "",
      section8Description: hdr.section8Description || "",
    };

    setFormItem(mapped);
  }, [data, initialItem]);

  const handleSave = async (formData: FormData) => {
    try {
      const dataStr = formData.get("data") as string | null;
      if (!dataStr) return;

      const json = JSON.parse(dataStr);
      json.id = Number(id);
      json.plant = plant;

      const payload = {
        ...json,
        id: Number(id),
        plant: plant,
      };

      // Wait for the mutation to complete and cache invalidation to trigger
      const result: any = await updateGlossary(payload).unwrap();
      
      notifications.show({
        message: result?.message,
        color: "green",
      });

      // Small delay to ensure cache invalidation has started
      setTimeout(() => {
        navigate("/admin/other-details/glossary", { 
          replace: true, // Use replace to prevent back navigation to edit form
        });
      }, 100);

    } catch (err: any) {
      console.error("Update error", err);
      notifications.show({
        message: err?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading && !formItem) {
    return (
      <div className="px-8 pt-6">
         <div className="flex items-center justify-center min-h-[200px]">
    <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
      </div>
    );
  }

  if (isError && !formItem) {
    return (
      <div className="px-8 pt-6">
        <p className="text-red-500">Failed to load glossary details.</p>
      </div>
    );
  }

  if (mode === "view") {
    return (
      <div className="px-8 pt-6">
        <GlossaryView item={formItem} onCancel={handleCancel} />
      </div>
    );
  }

  return (
    <div className="px-8 pt-6">
      <GlossaryForm
        item={formItem}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default AdminGlossaryEdit;